import fromUnixTime from 'date-fns/fromUnixTime';
import { isMerkleDropSchedule, isRangeEditionSchedule, type MintSchedule } from '@soundxyz/sdk';
import { getManyFromList } from '@soundxyz/utils/helpers';
import {
  getMintExpiry,
  isPublicSaleSoldOut,
  isScheduleSoldOut,
} from '@components/Song/MintComponents/helpers';
import type { ContractType, FragmentType, MintDataContractsFragmentDoc } from '@graphql/reactQuery';
import { useHasAccessToSchedule, useHasScheduleEligibility } from '@store/sdk/reactQuery';
import { bigIntToNumber } from '@utils/bigNumberHelpers';
import { useMintSchedule } from './useMintSchedule';

/**
 * two separate getter for merkle drop and range edition
 * to make typescript happy and keep code cleaner
 */
function getFirstMerkleDrop({ schedules }: { schedules: MintSchedule[] }) {
  const filteredSchedules = getManyFromList(schedules, sched =>
    isMerkleDropSchedule(sched) ? sched : null,
  );
  /**
   * in the case there are multiple presales or public sales, we want to show the one with the earliest start time
   */
  const sortedSchedule = filteredSchedules.sort((a, b) => {
    if (a.startTime === b.startTime) {
      const value = b.price - a.price;
      return bigIntToNumber(value, {
        strict: false,
      });
    }
    return a.startTime - b.startTime;
  });

  //return the first sched that has not expired
  const activeSchedule = sortedSchedule.find(sched => {
    const mintExpiryMs = fromUnixTime(getMintExpiry(sched)).getTime();

    return mintExpiryMs > Date.now();
  });

  return activeSchedule ?? sortedSchedule[sortedSchedule.length - 1] ?? null;
}

function getFirstRangeEdition({ schedules }: { schedules: MintSchedule[] | null }) {
  const filteredSchedules = getManyFromList(schedules, sched =>
    isRangeEditionSchedule(sched) ? sched : null,
  );
  /**
   * in the case there are multiple presales or public sales, we want to show the one with the earliest start time
   */
  const sortedSchedule = filteredSchedules.sort((a, b) => {
    if (a.startTime === b.startTime) {
      const value = b.price - a.price;
      return bigIntToNumber(value, {
        strict: false,
      });
    }
    return a.startTime - b.startTime;
  });

  //return the first sched that has not expired
  const activeSchedule = sortedSchedule.find(sched => {
    const mintExpiryMs = fromUnixTime(getMintExpiry(sched)).getTime();

    return mintExpiryMs > Date.now();
  });

  return activeSchedule ?? sortedSchedule[sortedSchedule.length - 1] ?? null;
}

/**
 *
 * @note mint schedules for regular mint types
 * - merkle drop
 * - range edition
 * - open edition
 *
 * @returns presale schedule and public sale schedule in addition to other data
 */
export const useGetRegularMintSchedules = ({
  release,
  chainId,
  auctionContractType,
  editionAddress,
  rootTotalMinted,
  isFirstPhaseCompleted,
  finalQuantity,
}: {
  release: FragmentType<MintDataContractsFragmentDoc>;
  chainId: number;
  auctionContractType: ContractType | null;
  editionAddress: string;
  rootTotalMinted: number;
  isFirstPhaseCompleted: boolean;
  finalQuantity: number | null;
}) => {
  const { schedules, areSchedulesLoading } = useMintSchedule({
    release,
  });
  const presaleSched = getFirstMerkleDrop({
    schedules,
  });

  const { data: hasPresaleAccess = false, isFetched: eligibilityFetched } =
    useHasScheduleEligibility({
      mintSchedule: presaleSched,
      editionAddress,
      auctionContractType,
    });

  const { data: canMintPresale = false, isFetched: presaleAccessFetched } = useHasAccessToSchedule({
    mintSchedule: presaleSched,
    editionAddress,
    chainId,
    auctionContractType,
    isFirstPhaseCompleted,
  });

  const publicSaleSchedule = getFirstRangeEdition({
    schedules,
  });
  const { data: canMintPublicSale = false, isFetched: publicAccessFetched } =
    useHasAccessToSchedule({
      mintSchedule: publicSaleSchedule,
      editionAddress,
      chainId,
      auctionContractType,
      isFirstPhaseCompleted,
    });

  const presaleMaxedOutPerWallet = hasPresaleAccess && !canMintPresale;
  const presaleSoldOut = presaleSched
    ? isScheduleSoldOut({
        schedule: presaleSched,
        collected: presaleSched.totalMinted,
      })
    : false;

  const publicSaleSoldOut = publicSaleSchedule
    ? isPublicSaleSoldOut({
        publicSaleSchedule,
        rootTotalMinted,
        finalQuantity,
      })
    : false;

  return {
    presale: presaleSched,
    hasPresaleAccess: hasPresaleAccess,
    publicSale: publicSaleSchedule,
    canMintPresale,
    canMintPublicSale,
    presaleMaxedOutPerWallet,
    presaleSoldOut,
    publicSaleSoldOut,
    loading:
      areSchedulesLoading ||
      (!eligibilityFetched && !!presaleSched) ||
      (!presaleAccessFetched && !!presaleSched) ||
      !publicAccessFetched,
  };
};
